"use client";

import type { ReverseGeocodeResponse } from "@/app/api/places/reverse-geocode/route";
import { useCallback, useState } from "react";

interface UseReverseGeocodeReturn {
  /** Reverse geocode coordinates to get location name */
  reverseGeocode: (
    lat: number,
    lon: number,
  ) => Promise<ReverseGeocodeResponse | null>;
  /** Whether reverse geocoding is in progress */
  isLoading: boolean;
  /** Error if reverse geocoding failed */
  error: string | null;
}

/**
 * Hook for reverse geocoding coordinates to get location names.
 */
export function useReverseGeocode(): UseReverseGeocodeReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reverseGeocode = useCallback(
    async (
      lat: number,
      lon: number,
    ): Promise<ReverseGeocodeResponse | null> => {
      setIsLoading(true);
      setError(null);

      try {
        const basePath = process.env.NEXT_PUBLIC_BASE_PATH || "";
        const params = new URLSearchParams({
          lat: String(lat),
          lon: String(lon),
        });

        const response = await fetch(
          `${basePath}/api/places/reverse-geocode?${params}`,
        );

        if (!response.ok) {
          throw new Error("Failed to reverse geocode location");
        }

        const data: ReverseGeocodeResponse = await response.json();
        return data;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to reverse geocode";
        setError(errorMessage);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [],
  );

  return {
    reverseGeocode,
    isLoading,
    error,
  };
}
